import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Bot, Send, Loader2, CloudUpload, History, RotateCcw, ChevronDown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const FIELD_LABELS = {
  system_prompt: "System prompt",
  first_message: "Greeting",
  model_provider: "AI provider",
  model_name: "AI model",
  voice: "Voice",
  start_speaking_plan: "Turn-taking (start)",
  stop_speaking_plan: "Turn-taking (stop)",
};

const changedFields = (proposal, current) =>
  Object.keys(FIELD_LABELS).filter(
    (k) => current && JSON.stringify(proposal?.[k]) !== JSON.stringify(current[k])
  );

const ProposalCard = ({ proposal, current, onApply, applying, applied }) => {
  const changes = changedFields(proposal, current);
  return (
    <div className="mt-2 border border-[#007AFF]/40 bg-[#0A0A0A] rounded-sm p-3" data-testid="copilot-proposal-card">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#007AFF] mb-2 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> Proposed changes
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(changes.length ? changes : ["no differences detected"]).map((c) => (
          <span key={c} className="text-[10px] font-mono px-1.5 py-0.5 border border-border text-muted-foreground rounded-sm">
            {FIELD_LABELS[c] || c}
          </span>
        ))}
      </div>
      <details className="mb-2">
        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-white flex items-center gap-1">
          <ChevronDown className="w-3 h-3" /> Preview full proposed config
        </summary>
        <pre className="mt-2 font-mono text-[10px] bg-background border border-border p-2 max-h-48 overflow-y-auto whitespace-pre-wrap scroll-thin text-muted-foreground">
          {JSON.stringify(proposal, null, 1)}
        </pre>
      </details>
      <Button size="sm" onClick={onApply} disabled={applying || applied} data-testid="copilot-apply-btn"
        className="rounded-sm gap-2 h-8 text-xs bg-[#007AFF] hover:bg-[#0066d6] text-white font-semibold">
        {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CloudUpload className="w-3.5 h-3.5" />}
        {applied ? "Applied ✓" : applying ? "Updating…" : "Update Vapi assistant"}
      </Button>
    </div>
  );
};

export const VapiCopilot = () => {
  const { user } = useAuth();
  const [assistants, setAssistants] = useState([]);
  const [selected, setSelected] = useState("");
  const [model, setModel] = useState("claude-opus-4-8");
  const [currentConfig, setCurrentConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [applyingIdx, setApplyingIdx] = useState(null);
  const [history, setHistory] = useState([]);
  const [reverting, setReverting] = useState(null);
  const sessionId = useRef(
    (window.crypto?.randomUUID && window.crypto.randomUUID()) || `cp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );
  const scrollRef = useRef(null);

  const isAdmin = user?.role === "admin";

  const loadHistory = (id) =>
    api.get(`/vapi/config-history?assistant_id=${id}`).then(({ data }) => setHistory(data)).catch(() => {});

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/vapi/assistants")
      .then(({ data }) => {
        setAssistants(data.assistants);
        const withPhone = data.assistants.find((a) => a.phone_numbers.length > 0);
        setSelected(data.selected_assistant_id || withPhone?.id || data.assistants[0]?.id || "");
      })
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!selected) return;
    api.get(`/vapi/assistant-config?assistant_id=${selected}`)
      .then(({ data }) => setCurrentConfig(data))
      .catch(() => setCurrentConfig(null));
    loadHistory(selected);
  }, [selected]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  if (!isAdmin) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || sending || !selected) return;
    setInput("");
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "admin", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/vapi/copilot/chat", {
        session_id: sessionId.current, assistant_id: selected, message: text, model,
      });
      setCurrentConfig((c) => ({ ...(c || {}), ...data.current_config }));
      setMessages((m) => [...m, {
        id: crypto.randomUUID(), role: "copilot", content: data.reply, proposal: data.proposal,
        baseConfig: data.current_config, applied: false,
      }]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Copilot failed to respond");
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "copilot", content: "Sorry — I hit an error. Please try that again." }]);
    } finally {
      setSending(false);
    }
  };

  const apply = async (idx) => {
    const msg = messages[idx];
    setApplyingIdx(idx);
    try {
      const { data } = await api.post("/vapi/apply-config", { assistant_id: selected, config: msg.proposal });
      toast.success(`"${data.assistant_name}" updated live on Vapi`);
      setMessages((m) => m.map((x, i) => (i === idx ? { ...x, applied: true } : x)));
      api.get(`/vapi/assistant-config?assistant_id=${selected}`).then(({ data: d }) => setCurrentConfig(d)).catch(() => {});
      loadHistory(selected);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setApplyingIdx(null);
    }
  };

  const revert = async (id) => {
    setReverting(id);
    try {
      const { data } = await api.post("/vapi/revert-config", { history_id: id });
      toast.success(`Reverted — "${data.assistant_name}" restored to the earlier config`);
      api.get(`/vapi/assistant-config?assistant_id=${selected}`).then(({ data: d }) => setCurrentConfig(d)).catch(() => {});
      loadHistory(selected);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Revert failed");
    } finally {
      setReverting(null);
    }
  };

  return (
    <div className="border border-border bg-[#0A0A0A] p-6 mt-8" data-testid="vapi-copilot-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2 mr-auto">
          <Bot className="w-4 h-4 text-[#007AFF]" /> Vapi Copilot — reshape your agent by chatting
        </div>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger data-testid="copilot-assistant-select" className="sm:w-56 h-8 rounded-sm bg-background border-border text-xs">
            <SelectValue placeholder="Assistant" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-border rounded-sm">
            {assistants.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name}{a.phone_numbers.length > 0 ? ` (${a.phone_numbers[0]})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={model} onValueChange={setModel}>
          <SelectTrigger data-testid="copilot-model-select" className="sm:w-44 h-8 rounded-sm bg-background border-border text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-border rounded-sm">
            <SelectItem value="claude-opus-4-8">Claude Opus 4.8</SelectItem>
            <SelectItem value="claude-fable-5">Claude Fable 5</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div ref={scrollRef} className="h-80 overflow-y-auto scroll-thin border border-border bg-background p-4 space-y-4" data-testid="copilot-chat-window">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-2 text-muted-foreground">
            <Bot className="w-8 h-8 text-[#007AFF]/60" />
            <p className="text-sm max-w-md">
              Tell me what to change about {currentConfig?.name || "your agent"} — its personality, greeting,
              voice, AI model, or how patiently it listens. I'll propose the change; you approve it with one click.
            </p>
            <p className="text-xs font-mono">e.g. "make her warmer and more reassuring to shaken callers"</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={m.id} className={m.role === "admin" ? "flex justify-end" : "flex justify-start"}>
            <div className={`max-w-[85%] rounded-sm px-3 py-2 text-sm leading-relaxed ${
              m.role === "admin" ? "bg-[#007AFF] text-white" : "bg-[#141416] border border-border"}`}>
              <p style={{ whiteSpace: "pre-wrap" }}>{m.content}</p>
              {m.proposal && (
                <ProposalCard proposal={m.proposal} current={m.baseConfig}
                  onApply={() => apply(i)} applying={applyingIdx === i} applied={m.applied} />
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="copilot-thinking">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-[#007AFF]" /> Copilot is thinking…
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-3">
        <Input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          placeholder='Ask for a change… e.g. "give her a calmer voice and greet callers by thanking them"'
          data-testid="copilot-input" disabled={sending}
          className="rounded-sm bg-background border-border focus-visible:ring-[#007AFF]" />
        <Button onClick={send} disabled={sending || !input.trim() || !selected} data-testid="copilot-send-btn"
          className="rounded-sm bg-[#007AFF] hover:bg-[#0066d6] text-white shrink-0 gap-2">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
        </Button>
      </div>

      {history.length > 0 && (
        <details className="mt-4" data-testid="copilot-history">
          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-white flex items-center gap-1.5">
            <History className="w-3.5 h-3.5" /> Version history ({history.length}) — revert if a change made things worse
          </summary>
          <div className="mt-2 space-y-1.5">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 border border-border bg-background px-3 py-2 text-xs">
                <div className="min-w-0 flex-1">
                  <span className="font-mono text-muted-foreground">{new Date(h.saved_at).toLocaleString()}</span>
                  <span className="text-muted-foreground"> · {h.model} · </span>
                  <span className="text-muted-foreground truncate">{h.prompt_preview}…</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => revert(h.id)} disabled={reverting === h.id}
                  data-testid={`copilot-revert-${h.id}`}
                  className="rounded-sm h-7 text-xs gap-1.5 border-border shrink-0">
                  {reverting === h.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  Revert to this
                </Button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};
